import { useCallback, useEffect, useRef, useState } from "react";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";

import IntlProvider from "./Utils/IntlProvider";
import { InjectIntlContext } from "./Intl/Context";
import { store, persistor } from "../config/reduxStore";
import { loadViewer } from "../reducers/viewer";
import { setPlatformTitle } from "../reducers/platform";
import { setIFrameMode } from "../reducers/styling";
import RSuiteProvider from "./RSuiteProvider";
import { useAppContainerProps } from "./AppContainer";

const AppReduxProvider = props => {
  const {
    locale,
    defaultLocale,
    translations,
    platformTitle,
    allowUsingPlatformInIFrame
  } = useAppContainerProps();

  const { children } = props;

  const [viewerLoaded, setViewerLoaded] = useState(false);
  const [viewerIsLoading, setViewerIsLoading] = useState(false);

  const clearViewerStoreSubscriptionRef = useRef();

  const clearViewerStoreSubscription = useCallback(() => {
    if (!clearViewerStoreSubscriptionRef.current) return;

    clearViewerStoreSubscriptionRef.current();
    clearViewerStoreSubscriptionRef.current = null;
  }, []);

  useEffect(() => {
    clearViewerStoreSubscription();

    clearViewerStoreSubscriptionRef.current = store.subscribe(() => {
      const { viewer } = store.getState();

      // Don't do anything if the viewer is not loaded.
      if (!viewer) return;

      const { id } = viewer;

      // Notify that the viewer was loaded and allow rendering sub
      if (!viewerLoaded && id) {
        setViewerLoaded(true);
        setViewerIsLoading(false);
      }
    });

    if (!viewerLoaded && !viewerIsLoading) {
      setViewerIsLoading(true);
      // Load the viewer object to the redux store so that other components can
      // use its associated data.
      store.dispatch(loadViewer());
    }

    return () => {
      clearViewerStoreSubscription();
    };
  }, [clearViewerStoreSubscription, viewerIsLoading, viewerLoaded]);

  // This sets the platform title to the store from the platformTitle prop that is passed
  // to the app container
  useEffect(() => {
    store.dispatch(setPlatformTitle(platformTitle));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformTitle]);

  // This fetches the `allowUsingPlatformInIFrame` prop from the default rails provided
  // props and checks whether the app is currently run in an iFrame. If it's the case, it
  // sets the iFrameMode to true in the redux store to make it available to all components
  // that have a specific behavior when the iFrameMode is enabled.
  //
  // This is processed once at the beginning of the app mounting, without any additional
  // data fetching to avoid header and footer be loaded and flash when loading the app in
  // an iframe.
  useEffect(() => {
    const isInIframe = window.location !== window.parent.location;

    if (!allowUsingPlatformInIFrame || !isInIframe) return;

    store.dispatch(setIFrameMode(true));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowUsingPlatformInIFrame]);

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <IntlProvider
          locale={locale}
          defaultLocale={defaultLocale}
          messages={translations}
        >
          <RSuiteProvider locale={locale}>
            <InjectIntlContext>{children}</InjectIntlContext>
          </RSuiteProvider>
        </IntlProvider>
      </PersistGate>
    </Provider>
  );
};

export default AppReduxProvider;
